// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
import {
  hasLocationDuplicateNameError,
  hasUniqueViolationError,
  hasZoneNameMatchesParentError,
} from '@/api/error-utils';
import { CompanySearch } from '@/components/company-search';
import { FormTextField } from '@/components/form-text-field';
import { SupportSnackbar } from '@/components/support-snackbar';
import {
  ArticleOutlined,
  InfoOutlined,
  TuneOutlined,
} from '@/constants/hardfin-standard-icons';
import {
  UpdateLocationParameters,
  updateLocation,
} from '@/location/api/update-location';
import {
  InventoryButtonGroup,
  InventoryMode,
  ZoneFormData,
  ZoneFormOutputData,
  zoneFormOutputSchema,
} from '@/location/components/zone-form-shared';
import { SparseLocation } from '@/sparse/api/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  ToggleButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { SubmitHandler, useController, useForm } from 'react-hook-form';

function deriveInventoryMode(zone: SparseLocation): InventoryMode {
  if (!zone.isInventoryOverride) {
    return 'inherit';
  }
  return zone.isInventory ? 'yes' : 'no';
}

interface Props {
  zone: SparseLocation;
  locationKey: string;
  isOpen: boolean;
  onClose: () => void;
}

export function EditZoneDialog(props: Props) {
  const queryClient = useQueryClient();

  const updateZoneMutation = useMutation({
    mutationFn: (parameters: UpdateLocationParameters) =>
      updateLocation(props.zone.key, 'ZONE', parameters),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['location', props.locationKey, 'zones'],
      });
      queryClient.invalidateQueries({
        queryKey: ['location', props.zone.key],
      });
      close();
    },
    onError: (error) => {
      if (
        hasUniqueViolationError(error) ||
        hasLocationDuplicateNameError(error)
      ) {
        setError('name', {
          type: 'custom',
          message: 'A zone with this name already exists at this location',
        });
      } else if (hasZoneNameMatchesParentError(error)) {
        setError('name', {
          type: 'custom',
          message: 'Zone name cannot be the same as the parent site name',
        });
      }
    },
  });

  const { control, formState, reset, setError, handleSubmit } = useForm<
    ZoneFormData,
    unknown,
    ZoneFormOutputData
  >({
    resolver: zodResolver(zoneFormOutputSchema),
    defaultValues: {
      name: props.zone.name,
      description: props.zone.description ?? '',
      consignee: props.zone.consignee ?? null,
      inventoryMode: deriveInventoryMode(props.zone),
    },
  });

  useEffect(() => {
    reset({
      name: props.zone.name,
      description: props.zone.description ?? '',
      consignee: props.zone.consignee ?? null,
      inventoryMode: deriveInventoryMode(props.zone),
    });
  }, [props.zone, reset]);

  const consigneeController = useController({ control, name: 'consignee' });
  const inventoryModeController = useController({
    control,
    name: 'inventoryMode',
  });

  const isAlertOpen =
    updateZoneMutation.isError &&
    !hasUniqueViolationError(updateZoneMutation.error) &&
    !hasLocationDuplicateNameError(updateZoneMutation.error) &&
    !hasZoneNameMatchesParentError(updateZoneMutation.error);

  const close = () => {
    props.onClose();
    reset();
  };

  const handleValidSubmission: SubmitHandler<ZoneFormOutputData> = (data) => {
    const parameters: UpdateLocationParameters = {};

    if (formState.dirtyFields.name) {
      parameters.name = data.name;
    }

    if (formState.dirtyFields.description) {
      parameters.description = data.description || null;
    }

    if (formState.dirtyFields.consignee) {
      const consigneeId =
        data.consignee && !data.consignee.isManufacturer
          ? data.consignee.id
          : null;
      parameters.consignee = consigneeId;
    }

    if (formState.dirtyFields.inventoryMode) {
      if (data.inventoryMode === 'inherit') {
        parameters.isInventoryOverride = false;
      } else {
        parameters.isInventoryOverride = true;
        parameters.isInventory = data.inventoryMode === 'yes';
      }
    }

    if (Object.keys(parameters).length > 0) {
      updateZoneMutation.mutate(parameters);
    }
  };

  return (
    <Dialog
      fullWidth
      maxWidth="xs"
      open={props.isOpen}
      onClose={close}
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmit(handleValidSubmission),
      }}
    >
      <SupportSnackbar
        isOpen={isAlertOpen}
        message="An error occurred while updating the zone."
        onClose={updateZoneMutation.reset}
      />
      <DialogTitle>Edit zone</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Box
            component="fieldset"
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              p: 1.5,
              m: 0,
            }}
          >
            <Typography
              component="legend"
              variant="subtitle2"
              sx={{
                px: 0.5,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <ArticleOutlined fontSize="small" color="action" />
              General
            </Typography>
            <Stack spacing={1.5}>
              <FormTextField name="name" label="Name" control={control} />
              <FormTextField
                name="description"
                label="Description"
                control={control}
                isMultiline
                minimumRows={2}
              />
              <CompanySearch
                label="Consignee (optional)"
                name="consignee"
                value={consigneeController.field.value}
                error={Boolean(consigneeController.fieldState.error)}
                helperText={
                  consigneeController.fieldState.error?.message ??
                  'Consignee is a customer that this zone is designated for (ex: reservations, provisioning, and 3PL)'
                }
                onChange={consigneeController.field.onChange}
                showManufacturer={false}
              />
            </Stack>
          </Box>
          <Box
            component="fieldset"
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              p: 1.5,
              m: 0,
            }}
          >
            <Typography
              component="legend"
              variant="subtitle2"
              sx={{
                px: 0.5,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <TuneOutlined fontSize="small" color="action" />
              Options
            </Typography>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              paddingX={1}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="body2">Include in inventory</Typography>
                <Tooltip
                  arrow
                  placement="right"
                  title="Assets at this location are considered in inventory for reporting purposes. 'Inherit' automatically uses the same treatment for this zone as the parent location's inventory treatment. Manually selecting 'yes' or 'no' will override the parent location inventory treatment. For example, a Warehouse (inventory) might have a Maintenance zone (non-inventory)."
                >
                  <InfoOutlined fontSize="small" color="action" />
                </Tooltip>
              </Stack>
              <Paper variant="outlined">
                <InventoryButtonGroup
                  exclusive
                  size="small"
                  value={inventoryModeController.field.value}
                  onChange={(_, value: InventoryMode | null) => {
                    if (value !== null) {
                      inventoryModeController.field.onChange(value);
                    }
                  }}
                >
                  <ToggleButton value="inherit">Inherit</ToggleButton>
                  <ToggleButton value="yes">Yes</ToggleButton>
                  <ToggleButton value="no">No</ToggleButton>
                </InventoryButtonGroup>
              </Paper>
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button size="small" onClick={close}>
          Cancel
        </Button>
        <LoadingButton
          size="small"
          type="submit"
          color="secondary"
          variant="contained"
          disabled={!formState.isDirty}
          loading={updateZoneMutation.isPending}
        >
          Save
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
